/**
 * This directive is used to detect a click event outside the specified element.
 * Usage:
 *       import clickoutside from '@/components/activix-ui/directives/clickoutside';
 *       app.directive('clickoutside', clickoutside());
 *
 * Then, in the template:
 * <div v-clickoutside="handleClickOutside">
 *    This is a div that will trigger the click outside event
 * </div>
 */

export interface ClickOutsideBinding {
    value: (event: MouseEvent | TouchEvent) => void;
    expression?: string;
    modifiers?: {
        bubble?: boolean;
    };
}

export interface ClickOutsideVNode {
    context?: {
        name?: string;
    };
    ctx?: {
        type?: {
            name?: string;
        };
    };
    component?: {
        type?: {
            name?: string;
        };
    };
}

export interface ClickOutsideDirective {
    beforeMount: (
        el: ClickOutsideHTMLElement,
        binding: ClickOutsideBinding,
        vNode: ClickOutsideVNode
    ) => void;
    unmounted: (el: ClickOutsideHTMLElement) => void;
}

export interface ClickOutsideHTMLElement extends HTMLElement {
    __vueClickOutside__?: (event: MouseEvent | TouchEvent) => void;
}

/**
 * Validates that the binding value is a function.
 */
export function validate(binding: ClickOutsideBinding, vNode: ClickOutsideVNode): boolean {
    if (typeof binding.value !== 'function') {
        const componentName =
            vNode?.ctx?.type?.name || vNode?.component?.type?.name || vNode?.context?.name;
        let warn = `[Vue-click-outside] Provided expression '${binding.expression}' is not a function, but has to be.`;

        if (componentName) {
            warn += ` Found in component '${componentName}'`;
        }

        // eslint-disable-next-line no-console
        console.warn(warn);
        return false;
    }

    return true;
}

/**
 * Determines whether click should trigger the callback.
 */
export function shouldTriggerCallback(
    el: HTMLElement,
    event: MouseEvent | TouchEvent,
    bubble: boolean
): boolean {
    if (bubble) {
        return true;
    }
    const target = event.target as Node;
    return !el.contains(target) && el !== target;
}

/**
 * Gets the appropriate event type based on device capabilities.
 */
export function getEventType(doc: Document = document): 'touchstart' | 'click' {
    return 'ontouchstart' in doc.documentElement ? 'touchstart' : 'click';
}

/**
 * Creates a click handler for the directive.
 */
export function createHandler(
    el: HTMLElement,
    binding: ClickOutsideBinding
): (event: MouseEvent | TouchEvent) => void {
    const bubble = binding.modifiers?.bubble || false;
    return (event: MouseEvent | TouchEvent) => {
        if (shouldTriggerCallback(el, event, bubble)) {
            binding.value(event);
        }
    };
}

export default (): ClickOutsideDirective => {
    return {
        beforeMount(el, binding, vNode) {
            if (!validate(binding, vNode)) {
                return;
            }

            const handler = createHandler(el, binding);
            el.__vueClickOutside__ = handler;

            const eventType = getEventType();
            document.addEventListener(eventType, handler);
        },

        unmounted(el) {
            const eventType = getEventType();
            if (el.__vueClickOutside__) {
                document.removeEventListener(eventType, el.__vueClickOutside__);
                delete el.__vueClickOutside__;
            }
        },
    };
};
